// =====================================================================
// WeddingOS – /guest/music  (Musikwunsch)
// =====================================================================
// Flow:
//   1. Suchfeld → Debounced Live-Suche via Spotify API
//   2. Ergebnisliste mit Cover, Titel, Artist
//   3. Song auswählen → optionale Nachricht → Absenden
//   4. Bestätigung → eigene Wünsche (mit Status) via Realtime
// =====================================================================

import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { searchTracks } from "../../services/spotify";
import { submitMusicRequest, realtime, supabase } from "../../services/supabase";
import { loadGuestSession } from "../../lib/guestSession";
import InfoTip from "../../components/InfoTip";

const MUSIC_STEPS = [
  { title: "Song suchen", detail: "Gib Songtitel oder Kuenstlernamen ein - Ergebnisse erscheinen sofort." },
  { title: "Song auswaehlen", detail: "Tippe auf den gewuenschten Song. Du siehst Cover, Titel und Kuenstler." },
  { title: "Nachricht hinzufuegen", detail: "Schreibe optional eine persoenliche Nachricht zum Song dazu." },
  { title: "Wunsch absenden", detail: "Tippe auf Wunsch einreichen - das Brautpaar entscheidet ob er gespielt wird." },
  { title: "Status verfolgen", detail: "Sieh darunter ob dein Wunsch angenommen oder abgelehnt wurde." },
];

const DEBOUNCE_MS = 380;

export default function GuestMusic() {
  const navigate = useNavigate();
  const session  = loadGuestSession();

  // ── Suche ──────────────────────────────────────────────────────────
  const [query,   setQuery]   = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const debounceRef = useRef(null);

  // ── Auswahl / Formular ─────────────────────────────────────────────
  const [selected, setSelected]     = useState(null);
  const [message,  setMessage]      = useState("");
  const [submitting, setSubmitting] = useState(false);

  // ── Meine Wünsche (mit Realtime-Status) ───────────────────────────
  const [myRequests, setMyRequests] = useState([]);

  // ── Fehler ─────────────────────────────────────────────────────────
  const [error, setError] = useState(null);
  const [done,  setDone]  = useState(false);

  // Feature-Check: leitet zurück wenn Hochzeit nicht Premium (Musik ist Premium)
  useEffect(() => {
    if (!session?.wedding_id) return;
    supabase
      .rpc("get_wedding_features", { p_wedding_id: session.wedding_id })
      .then(({ data }) => {
        const f = Array.isArray(data) ? data[0] : data;
        if (f && f.is_paid !== true) navigate("/guest", { replace: true });
      })
      .catch(() => {});
  }, [session?.wedding_id]);

  // Realtime: Status-Updates für meine Wünsche
  useEffect(() => {
    if (!session?.wedding_id) return;
    const unsub = realtime.musicQueue(session.wedding_id, (payload) => {
      if (payload.eventType === "UPDATE") {
        setMyRequests((prev) =>
          prev.map((r) => (r.id === payload.new.id ? { ...r, status: payload.new.status } : r))
        );
      }
    });
    return unsub;
  }, [session?.wedding_id]);

  // Debounced Spotify-Suche
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (query.trim().length < 2) { setResults([]); setSearching(false); return; }

    setSearching(true);
    debounceRef.current = setTimeout(async () => {
      try {
        const tracks = await searchTracks(query);
        setResults(tracks);
      } catch {
        setResults([]);
      } finally {
        setSearching(false);
      }
    }, DEBOUNCE_MS);

    return () => clearTimeout(debounceRef.current);
  }, [query]);

  const handleSelect = useCallback((track) => {
    setSelected(track);
    setResults([]);
    setQuery("");
    setError(null);
    setDone(false);
  }, []);

  const handleCancel = useCallback(() => {
    setSelected(null);
    setMessage("");
    setError(null);
  }, []);

  const handleSubmit = useCallback(async () => {
    if (!selected) return;
    if (!session?.guest_id) {
      setError("Sitzung ungültig – bitte neu über den Tisch-Link einloggen.");
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const req = await submitMusicRequest({ guestId: session.guest_id, track: selected, message });
      setMyRequests((prev) => [req, ...prev]);
      setSelected(null);
      setMessage("");
      setDone(true);
      setTimeout(() => setDone(false), 4000);
    } catch (err) {
      setError("Leider hat das nicht geklappt. Bitte nochmal versuchen.");
      console.error(err);
    } finally {
      setSubmitting(false);
    }
  }, [selected, session, message]);

  if (!session) {
    return (
      <div className="wos-shell" style={{ textAlign: "center", paddingTop: 80 }}>
        <p className="wos-meta">Sitzung abgelaufen. Bitte Einladungslink neu öffnen.</p>
      </div>
    );
  }

  return (
    <div className="wos-shell">
      {/* ── Header ── */}
      <PageHeader onBack={() => navigate("/guest")} title="Musikwunsch" />

      {/* ── Anleitung ── */}
      {!selected && !done && (
        <InfoTip
          short="🎵 Suche nach einem Song und schick uns deinen Wunsch."
          steps={MUSIC_STEPS}
        />
      )}

      {/* ── Bestätigung ── */}
      {done && <SuccessBanner message="Wunsch eingereicht! ✦" />}

      {/* ── Formular: Song ausgewählt ── */}
      {selected ? (
        <SelectedSongForm
          track={selected}
          message={message}
          onMessageChange={setMessage}
          onSubmit={handleSubmit}
          onCancel={handleCancel}
          submitting={submitting}
          error={error}
        />
      ) : (
        <>
          {/* ── Suchfeld ── */}
          <SearchField
            value={query}
            onChange={setQuery}
            searching={searching}
          />

          {/* ── Suchergebnisse ── */}
          {results.length > 0 && (
            <TrackList tracks={results} onSelect={handleSelect} />
          )}

          {/* ── Leer-Zustand ── */}
          {!query && results.length === 0 && (
            <EmptyState />
          )}
        </>
      )}

      {/* ── Meine Wünsche ── */}
      {myRequests.length > 0 && !selected && (
        <MyRequests requests={myRequests} />
      )}
    </div>
  );
}

// =====================================================================
// PageHeader
// =====================================================================

function PageHeader({ onBack, title }) {
  return (
    <header className="wos-rise" style={{
      display:        "flex",
      alignItems:     "center",
      gap:            12,
      marginBottom:   28,
    }}>
      <button
        onClick={onBack}
        aria-label="Zurück"
        style={{
          background:   "var(--surface)",
          border:       "1px solid var(--line)",
          borderRadius: "var(--radius-md)",
          width:        40,
          height:       40,
          display:      "flex",
          alignItems:   "center",
          justifyContent: "center",
          fontSize:     18,
          color:        "var(--ink-muted)",
          flexShrink:   0,
          cursor:       "pointer",
        }}
      >←</button>
      <div>
        <p className="wos-eyebrow">Gast-Aktion</p>
        <h1 style={{ fontFamily: "var(--font-display)", fontSize: "1.7rem", marginTop: 2 }}>
          {title}
        </h1>
      </div>
    </header>
  );
}

// =====================================================================
// SearchField
// =====================================================================

function SearchField({ value, onChange, searching }) {
  const ref = useRef(null);
  useEffect(() => { ref.current?.focus(); }, []);

  return (
    <div className="wos-rise wos-rise-delay-1" style={{ position: "relative", marginBottom: 16 }}>
      <label className="wos-label" htmlFor="music-search">Song oder Künstler suchen</label>
      <input
        ref={ref}
        id="music-search"
        type="search"
        className="wos-input"
        placeholder="z.B. Can't Help Falling in Love …"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        autoComplete="off"
        autoCorrect="off"
        spellCheck={false}
        style={{ paddingRight: 44 }}
      />
      {searching && (
        <div style={{
          position:  "absolute",
          right:     14,
          bottom:    14,
        }}>
          <div className="wos-spinner" style={{
            borderColor: "rgba(183,110,121,0.3)",
            borderTopColor: "var(--accent)",
          }}/>
        </div>
      )}
    </div>
  );
}

// =====================================================================
// TrackList
// =====================================================================

function TrackList({ tracks, onSelect }) {
  return (
    <ul
      role="listbox"
      aria-label="Suchergebnisse"
      style={{ listStyle: "none", padding: 0, margin: 0 }}
    >
      {tracks.map((track, i) => (
        <TrackRow key={track.id} track={track} index={i} onSelect={onSelect} />
      ))}
    </ul>
  );
}

function TrackRow({ track, index, onSelect }) {
  const [pressed, setPressed] = useState(false);

  return (
    <li
      role="option"
      aria-selected="false"
      className={`wos-rise`}
      style={{ animationDelay: `${index * 0.04}s` }}
    >
      <button
        onClick={() => onSelect(track)}
        onPointerDown={() => setPressed(true)}
        onPointerUp={() => setPressed(false)}
        onPointerLeave={() => setPressed(false)}
        style={{
          display:       "flex",
          alignItems:    "center",
          gap:           14,
          width:         "100%",
          padding:       "12px 14px",
          marginBottom:  8,
          background:    pressed ? "var(--surface)" : "var(--bg-elevated)",
          border:        "1px solid var(--line)",
          borderRadius:  "var(--radius-md)",
          cursor:        "pointer",
          transform:     pressed ? "scale(0.99)" : "scale(1)",
          transition:    "transform 0.1s ease, background 0.1s ease",
          textAlign:     "left",
        }}
      >
        {/* Cover */}
        <AlbumCover url={track.cover} alt={track.title} size={48} />

        {/* Meta */}
        <div style={{ flex: 1, overflow: "hidden" }}>
          <p style={{
            margin:       0,
            fontSize:     15,
            fontWeight:   500,
            color:        "var(--ink)",
            whiteSpace:   "nowrap",
            overflow:     "hidden",
            textOverflow: "ellipsis",
          }}>{track.title}</p>
          <p style={{
            margin:       "3px 0 0",
            fontSize:     13,
            color:        "var(--ink-muted)",
            whiteSpace:   "nowrap",
            overflow:     "hidden",
            textOverflow: "ellipsis",
          }}>{track.artist}</p>
        </div>

        <span style={{ color: "var(--accent)", fontSize: 18, flexShrink: 0 }}>+</span>
      </button>
    </li>
  );
}

// =====================================================================
// SelectedSongForm
// =====================================================================

function SelectedSongForm({ track, message, onMessageChange, onSubmit, onCancel, submitting, error }) {
  return (
    <div className="wos-rise">
      {/* Song-Card */}
      <div className="wos-card" style={{ display: "flex", gap: 16, alignItems: "center", marginBottom: 24 }}>
        <AlbumCover url={track.cover} alt={track.title} size={64} />
        <div style={{ flex: 1, overflow: "hidden" }}>
          <p style={{ margin: 0, fontFamily: "var(--font-display)", fontSize: "1.2rem" }}>
            {track.title}
          </p>
          <p className="wos-meta" style={{ marginTop: 4 }}>{track.artist}</p>
          {track.album && (
            <p style={{ margin: "2px 0 0", fontSize: 12, color: "var(--ink-subtle)" }}>{track.album}</p>
          )}
        </div>
      </div>

      {/* Optionale Nachricht */}
      <div className="wos-field">
        <label className="wos-label" htmlFor="song-message">
          Nachricht zum Song <span style={{ color: "var(--ink-subtle)", textTransform: "none", letterSpacing: 0 }}>(optional)</span>
        </label>
        <textarea
          id="song-message"
          className="wos-textarea"
          rows={3}
          placeholder={'z.B. "Das ist unser Lied!" …'}
          value={message}
          onChange={(e) => onMessageChange(e.target.value)}
          maxLength={200}
          style={{ resize: "none" }}
        />
        <p style={{ textAlign: "right", margin: "4px 0 0", fontSize: 12, color: "var(--ink-subtle)" }}>
          {message.length}/200
        </p>
      </div>

      {error && <div className="wos-error">{error}</div>}

      <button
        className="wos-btn wos-btn-primary"
        onClick={onSubmit}
        disabled={submitting}
        style={{ marginBottom: 12 }}
      >
        {submitting ? <><span className="wos-spinner" />Wird eingereicht …</> : "Wunsch einreichen ✦"}
      </button>

      <button className="wos-btn wos-btn-ghost" onClick={onCancel} disabled={submitting}>
        Anderen Song wählen
      </button>
    </div>
  );
}

// =====================================================================
// MyRequests
// =====================================================================

const STATUS_CONFIG = {
  pending:  { label: "Ausstehend",  color: "var(--ink-muted)",   bg: "var(--surface)" },
  approved: { label: "Angenommen ✓", color: "var(--forest)",     bg: "var(--forest-soft)" },
  rejected: { label: "Nicht möglich", color: "var(--ink-subtle)", bg: "var(--surface)" },
};

function MyRequests({ requests }) {
  return (
    <div className="wos-rise" style={{ marginTop: 36 }}>
      <div className="wos-divider" style={{ marginBottom: 20 }}>
        <span className="wos-divider-icon" style={{ fontSize: 10, letterSpacing: "0.25em", color: "var(--ink-subtle)" }}>
          MEINE WÜNSCHE
        </span>
      </div>
      <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
        {requests.map((r) => {
          const cfg = STATUS_CONFIG[r.status] ?? STATUS_CONFIG.pending;
          return (
            <li key={r.id} style={{
              display:      "flex",
              alignItems:   "center",
              gap:          12,
              padding:      "12px 14px",
              background:   cfg.bg,
              border:       "1px solid var(--line)",
              borderRadius: "var(--radius-md)",
              marginBottom: 8,
            }}>
              <AlbumCover url={r.album_cover_url} alt={r.track_title} size={40} />
              <div style={{ flex: 1, overflow: "hidden" }}>
                <p style={{ margin: 0, fontSize: 14, fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {r.track_title}
                </p>
                <p style={{ margin: "2px 0 0", fontSize: 12, color: "var(--ink-muted)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {r.artist}
                </p>
              </div>
              <span style={{
                flexShrink:   0,
                fontSize:     11,
                fontWeight:   500,
                color:        cfg.color,
                letterSpacing: "0.06em",
              }}>
                {cfg.label}
              </span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

// =====================================================================
// Helpers
// =====================================================================

function AlbumCover({ url, alt, size }) {
  return (
    <div style={{
      width:        size,
      height:       size,
      flexShrink:   0,
      borderRadius: "var(--radius-sm)",
      overflow:     "hidden",
      background:   "var(--surface)",
      display:      "flex",
      alignItems:   "center",
      justifyContent: "center",
    }}>
      {url
        ? <img src={url} alt={alt} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
        : <span style={{ fontSize: size * 0.35, color: "var(--ink-subtle)" }}>♩</span>
      }
    </div>
  );
}

function SuccessBanner({ message }) {
  return (
    <div role="status" style={{
      padding:      "14px 18px",
      background:   "var(--forest-soft)",
      border:       "1px solid var(--forest)",
      borderRadius: "var(--radius-md)",
      marginBottom: 20,
      color:        "var(--forest)",
      fontWeight:   500,
      fontSize:     15,
      animation:    "wos-rise 0.3s ease",
      textAlign:    "center",
    }}>
      {message}
    </div>
  );
}

function EmptyState() {
  return (
    <div className="wos-rise wos-rise-delay-2" style={{ textAlign: "center", paddingTop: 40 }}>
      <p style={{ fontSize: 32, color: "var(--accent)", marginBottom: 12 }}>♩</p>
      <p style={{ fontFamily: "var(--font-display)", fontSize: "1.4rem", color: "var(--ink-muted)" }}>
        Welchen Song soll die Band spielen?
      </p>
      <p className="wos-meta" style={{ marginTop: 8, fontSize: 13 }}>
        Gib Songtitel oder Künstlernamen ein.
      </p>
    </div>
  );
}